import { ref, watch, computed } from '@vue/composition-api'
import { useToast } from 'vue-toastification/composition'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import Vue from 'vue'
import axios from 'axios'
import apiEndpoints from '@/constants/apiEndpoints'
import router from '../../../../router'

export default function useRegionsList() {
  const toast = useToast()
  const refRegionListTable = ref(null)

  const tableColumns = [
    { key: 'region_name', sortable: true, label: 'Region Name' },
    { key: 'region_code', sortable: true, label: 'Region Code' },
    { key: 'users', sortable: false, label: 'Manager' },
    { key: 'actions' },
  ]

  const perPage = ref(10)
  const totalRegions = ref(0)
  const currentPage = ref(1)
  const perPageOptions = [10, 25, 50, 100]
  const searchQuery = ref('')
  const sortBy = ref('region_name')
  const isSortDirDesc = ref(true)
  const roleFilter = ref(null)
  const statusFilter = ref(null)
  const loading = ref(false)

  const dataMeta = computed(() => {
    const localItemsCount = refRegionListTable.value ? refRegionListTable.value.localItems.length : 0
    return {
      from: perPage.value * (currentPage.value - 1) + (localItemsCount ? 1 : 0),
      to: perPage.value * (currentPage.value - 1) + localItemsCount,
      of: totalRegions.value,
    }
  })

  const refetchData = () => {
    if (refRegionListTable.value) {
      refRegionListTable.value.refresh()
    }
  }

  watch([currentPage, perPage, searchQuery, roleFilter, statusFilter, sortBy, isSortDirDesc], () => {
    refetchData()
  })

  const fetchRegions = (ctx, callback) => {
    const to = perPage.value * (currentPage.value - 1)
    const userToken = Vue.$cookies.get('userToken')
    const headers = {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${userToken}`,
    }

    loading.value = true
    axios
      .get(`${process.env.VUE_APP_API_URL}${apiEndpoints.API_GET_REGION_WITH_USERS}`, {
        headers,
        params: {
          search: searchQuery.value,
          order_by: sortBy.value,
          order_sort: isSortDirDesc.value ? 'DESC' : 'ASC',
          limit: perPage.value,
          offset: to,
        },
      })
      .then(response => {
        if (response.data && response.data.data !== null) {
          totalRegions.value = response.data.data_count || 0
          callback(response.data.data)
        } else {
          callback([])
        }
        loading.value = false
      })
      .catch(e => {
        console.error(e)
        if (e.message === 'Request failed with status code 401') {
          router.push({ name: 'auth-login' })
        } else {
          toast({
            component: ToastificationContent,
            props: {
              title: 'Error fetching data list',
              icon: 'AlertTriangleIcon',
              variant: 'danger',
            },
          })
        }
        loading.value = false
      })
  }

  const fetchRoles = () => {
    const userToken = Vue.$cookies.get('userToken')
    const headers = {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${userToken}`,
    }

    return axios
      .get(`${process.env.VUE_APP_API_URL}${apiEndpoints.API_GET_ROLES}`, { headers })
      .then(response => {
        if (response.data && response.data.data) {
          return response.data.data.map(elem => ({
            label: elem.role_name,
            value: elem.role_id,
          }))
        }
        return [
          { label: 'Admin', value: 'admin' },
          { label: 'Author', value: 'author' },
          { label: 'Editor', value: 'editor' },
          { label: 'Maintainer', value: 'maintainer' },
          { label: 'Subscriber', value: 'subscriber' },
        ]
      })
      .catch(() => {
        toast({
          component: ToastificationContent,
          props: {
            title: 'Error fetching role list',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
        return []
      })
  }

  // *===============================================---*
  // *--------- UI ---------------------------------------*
  // *===============================================---*

  const resolveUserRoleVariant = role => {
    if (role === 'subscriber') return 'primary'
    if (role === 'author') return 'warning'
    if (role === 'maintainer') return 'success'
    if (role === 'editor') return 'info'
    if (role === 'super-admin') return 'danger'
    return 'primary'
  }

  const resolveUserRoleIcon = role => {
    if (role === 'subscriber') return 'UserIcon'
    if (role === 'author') return 'SettingsIcon'
    if (role === 'maintainer') return 'DatabaseIcon'
    if (role === 'editor') return 'Edit2Icon'
    if (role === 'super-admin') return 'ServerIcon'
    return 'UserIcon'
  }

  const resolveUserStatusVariant = status => {
    if (status === 'pending') return 'warning'
    if (status === 'active') return 'success'
    if (status === 'inactive') return 'secondary'
    return 'primary'
  }

  return {
    fetchRegions,
    fetchRoles,
    tableColumns,
    perPage,
    currentPage,
    totalRegions,
    dataMeta,
    perPageOptions,
    searchQuery,
    sortBy,
    isSortDirDesc,
    refRegionListTable,
    resolveUserRoleVariant,
    resolveUserRoleIcon,
    resolveUserStatusVariant,
    refetchData,
    roleFilter,
    statusFilter,
    loading,
  }
}